import React, { createContext, useContext, useMemo, useEffect, useRef } from "react";
import useAttendanceData from "./useAttendanceData";
import useAttendanceFilters from "./useAttendanceFilters";
import useGroupList from "./useGroupList";
import useObjectivePlannedHours from "./useObjectivePlannedHours";
import usePlannedShifts from "./usePlannedShifts";

const TIME_LIST_VIEWS = {
	list: 1,
	employee_day: 2,
	employee_week: 3,
};

const AttendanceContext = createContext();

/**
 * Provider para la funcionalidad de Attendance Supervisor
 * @param {number} timeListView - Tipo de vista actual (list / employee_day / employee_week)
 */
export const AttendanceProvider = ({
	children,
	timeListView = TIME_LIST_VIEWS.list,
	timeRegistrationBalanceCalculation,
}) => {
	const filters = useAttendanceFilters();

	const groupList = useGroupList();

	const hasValidatedGroupRef = useRef(false);
	const lastGroupsLengthRef = useRef(0);
	
	useEffect(() => {
		const groupsChanged = lastGroupsLengthRef.current !== (groupList.groups?.length || 0);
		
		if (!groupList.loading && filters.groupId && (!hasValidatedGroupRef.current || groupsChanged)) {
			hasValidatedGroupRef.current = true;
			lastGroupsLengthRef.current = groupList.groups?.length || 0;
			
			if (groupList.groups && groupList.groups.length > 0) {
				const groupExists = groupList.groups.some(group => String(group.id) === String(filters.groupId));
				if (!groupExists) {
					filters.handleGroupChange("");
				}
			} else if (groupList.groups && groupList.groups.length === 0) {
				filters.handleGroupChange("");
			}
		}
		if (groupList.loading) {
			hasValidatedGroupRef.current = false;
		}
	}, [groupList.loading, groupList.groups, filters.groupId, filters.handleGroupChange]);

	// Planned-hours solo se llama cuando la vista es "agrupar por día"
	const isEmployeeDayView = timeListView === TIME_LIST_VIEWS.employee_day;
	const hoursData = useObjectivePlannedHours(filters.filterParams, {
		enabled: isEmployeeDayView,
	});

	// Turnos planificados (para la columna "Turno planificado" en ambas vistas)
	const plannedShiftsData = usePlannedShifts(filters.filterParams);

	// useAttendanceData notifica con los mismos employee IDs que calcula para alertas
	const attendanceData = useAttendanceData(filters.filterParams, {
		onNewEmployeeIds: (ids) => {
			hoursData.fetchForEmployees(ids);
			plannedShiftsData.fetchForEmployees(ids);
		},
	});

	const contextValue = useMemo(
		() => ({
			items: attendanceData.items || [],
			loading: attendanceData.loading,
			loadingMore: attendanceData.loadingMore,
			error: attendanceData.error,
			hasMore: attendanceData.hasMore,
			fetchMoreData: attendanceData.fetchMoreData,
			handleSort: attendanceData.handleSort,
			applySort: attendanceData.applySort,
			sortConfig: attendanceData.sortConfig,
			handleApprove: attendanceData.handleApprove,
			approvingIds: attendanceData.approvingIds,
			setItems: attendanceData.setItems,
			stats: attendanceData.stats,

			objectiveHours: hoursData.objectiveHours || [],
			plannedHours: hoursData.plannedHours || [],
			plannedShifts: plannedShiftsData.plannedShifts || {},

			filters: {
				dateRange: filters.dateRange,
				activeDatePreset: filters.activeDatePreset,
				employeeIds: filters.employeeIds,
				groupId: filters.groupId,
				showApproved: filters.showApproved,
				showAlerts: filters.showAlerts,
				notEnded: filters.notEnded,
				showRestTime: filters.showRestTime,
				handleDateChange: filters.handleDateChange,
				setDatePreset: filters.setDatePreset,
				handleEmployeeChange: filters.handleEmployeeChange,
				handleGroupChange: filters.handleGroupChange,
				toggleFilter: filters.toggleFilter,
				setFilterAlerts: filters.setFilterAlerts,
			},

			groups: {
				list: groupList.groups || [],
				loading: groupList.loading,
				error: groupList.error,
				searchTerm: groupList.searchTerm,
				handleSearch: groupList.handleSearch,
			},

			timeRegistrationBalanceCalculation,
		}),
		[
			attendanceData.items,
			attendanceData.loading,
			attendanceData.loadingMore,
			attendanceData.error,
			attendanceData.hasMore,
			attendanceData.handleSort,
			attendanceData.applySort,
			attendanceData.sortConfig,
			attendanceData.handleApprove,
			attendanceData.approvingIds,
			attendanceData.stats,
			hoursData.objectiveHours,
			hoursData.plannedHours,
			plannedShiftsData.plannedShifts,
			filters.dateRange,
			filters.employeeIds,
			filters.groupId,
			filters.showApproved,
			filters.showAlerts,
			filters.notEnded,
			filters.showRestTime,
			groupList.groups,
			groupList.loading,
			groupList.error,
			groupList.searchTerm,
			timeRegistrationBalanceCalculation,
		]
	);

	return (
		<AttendanceContext.Provider value={contextValue}>
			{children}
		</AttendanceContext.Provider>
	);
};

export const useAttendance = () => {
	const context = useContext(AttendanceContext);
	if (!context) {
		throw new Error("useAttendance debe ser usado dentro de un AttendanceProvider");
	}
	return context;
};
